import { useQuery, useMutation } from '@tanstack/react-query'
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query'
import { PostgrestError } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'

// Generic error type for Supabase operations
export type SupabaseError = PostgrestError | Error | null

// Helper to convert PostgrestError to Error
export const toError = (error: SupabaseError): Error | null => {
  if (!error) return null
  if (error instanceof Error) return error
  return new Error((error as PostgrestError).message || 'An error occurred')
}

// Generic query hook for Supabase
export function useSupabaseQuery<TData = unknown>(
  queryKey: unknown[],
  queryFn: () => Promise<{ data: TData | null; error: PostgrestError | null }>,
  options?: Omit<UseQueryOptions<TData, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await queryFn()
      
      if (error) throw toError(error)
      
      // React Query expects either a value or a thrown error, never undefined
      // Return null if data is null or undefined
      return data ?? null as TData
    },
    ...options,
  })
}

// Generic mutation hook for Supabase
export function useSupabaseMutation<TData = unknown, TVariables = unknown>(
  mutationFn: (variables: TVariables) => Promise<{ data: TData | null; error: PostgrestError | null }>,
  options?: UseMutationOptions<TData, Error, TVariables>
) {
  return useMutation({
    mutationFn: async (variables: TVariables) => {
      const { data, error } = await mutationFn(variables)
      if (error) throw toError(error)
      
      // React Query expects either a value or a thrown error, never undefined
      // Return null if data is null or undefined
      return data ?? null as TData
    },
    ...options,
  })
}

// Helper to call Supabase functions
export async function callSupabaseFunction<TArgs = unknown, TResponse = unknown>(
  functionName: string,
  args?: TArgs
): Promise<{ data: TResponse | null; error: PostgrestError | null }> {
  try {
    const { data, error } = await supabase.rpc(functionName, args as Record<string, unknown>)
    
    // Handle case where Supabase returns the JSON directly (not wrapped)
    // This can happen with JSON/JSONB return types
    if (data && typeof data === 'object' && !error) {
      // If the data looks like our expected response structure, return it directly
      if ('success' in data || 'reports' in data || 'error' in data) {
        return { data: data as TResponse, error: null }
      }
    }
    
    return { data: data as TResponse, error }
  } catch (err) {
    console.error(`[callSupabaseFunction] ${functionName} error:`, err);
    throw err;
  }
}

// Helper for real-time subscriptions
export function useSupabaseSubscription<T = unknown>(
  _channel: string,
  _event: string,
  _callback: (payload: T) => void
) {
  // This will be implemented when we need real-time features
  // For now, it's a placeholder
  return null
}